import { memo } from "react";
import { Wand2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { formatMoneyShort } from "@/components/Plan/MoneyInput";
import DayPricePopover from "./DayPricePopover";
import ProductNamePopover from "./ProductNamePopover";
import QtyCellInput from "./QtyCellInput";
import { dayUnitPrice } from "../utils/dayUnitPrice";
import { type CellStatus } from "../utils/useSaveStatus";
import type { DailyRow, PriceLevelInfo } from "./BottomUpDailyGrid";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";

function formatQty(n: number): string {
  if (!n) return "—";
  return n.toLocaleString("en-US");
}

interface Props {
  row: DailyRow;
  planCurrency?: string | null;
  displayName: string;
  days: string[];
  editable: boolean;
  isLocked: (iso: string) => boolean;
  rowIndex: number;
  isSelected: (rowIndex: number, colIndex: number) => boolean;
  onCellMouseDown: (rowIndex: number, colIndex: number, shiftKey: boolean) => void;
  onCellCommit: (
    productExternalId: string,
    periodDate: string,
    quantity: number | null,
    fillSelection?: boolean,
  ) => void;
  onPasteRow: (productExternalId: string, anchorDate: string, text: string) => void;
  saveStatusFor?: (productExternalId: string, periodDate: string) => CellStatus | undefined;
  pricingByDate: Record<string, string>;
  pricingLabelsByDate: Record<string, string>;
  priceLevelById: Map<string, PriceLevelInfo>;
  preview: number | null;
  onPreview: (value: number | null) => void;
  onDistributeRow?: (row: DailyRow) => void;
}

function DailyProductRow({
  row,
  planCurrency,
  displayName,
  days,
  editable,
  isLocked,
  rowIndex,
  isSelected,
  onCellMouseDown,
  onCellCommit,
  onPasteRow,
  saveStatusFor,
  pricingByDate,
  pricingLabelsByDate,
  priceLevelById,
  preview,
  onPreview,
  onDistributeRow,
}: Props) {
  const currency = row.asp?.currency ?? planCurrency;
  const cellByDate = new Map(row.days.map((c) => [c.period_date, c]));
  const rowSum = row.days.reduce((s, c) => s + (c.quantity || 0), 0);
  const delta = rowSum - row.month_target;

  return (
    <tr style={{ borderColor: "var(--accent)" }} className="border-b">
      <td
        style={{ background: "var(--background)", borderColor: "var(--border)", width: PLAN_PRODUCT_COL_WIDTH }}
        className="sticky left-0 z-10 px-3 py-2 border-r overflow-hidden"
      >
        <ProductNamePopover
          externalId={row.product_external_id}
          displayName={displayName}
          fullName={row.product_name}
          asp={row.asp}
          cogs={row.cogs}
        />
      </td>
      {days.map((d, colIndex) => {
        const cell = cellByDate.get(d);
        const quantity = cell?.quantity ?? 0;
        const locked = isLocked(d);
        const cellEditable = editable && !locked;
        const selected = cellEditable && isSelected(rowIndex, colIndex);
        const status = saveStatusFor?.(row.product_external_id, d);
        const levelId = pricingByDate[d];
        const level = levelId ? priceLevelById.get(levelId) : undefined;
        const unit = dayUnitPrice(row.prices_by_level, row.asp?.price, levelId);
        return (
          <td
            key={d}
            className="px-1 py-0.5 relative"
            style={level?.color ? { background: `color-mix(in srgb, ${level.color} 10%, transparent)` } : undefined}
          >
            {cell?.manual && !locked && (
              <span
                aria-hidden
                title="Manually entered — Distribute keeps this cell"
                style={{ background: "var(--muted-foreground)", width: 4, height: 4 }}
                className="absolute top-1 right-1 rounded-full opacity-60"
              />
            )}
            {locked ? (
              // Elapsed day: planned quantity as plain text, same as By Month's
              // locked months. Blank when nothing was planned.
              <span
                style={{ color: "var(--foreground)" }}
                className="block w-full text-right text-xs tabular-nums px-1.5 py-1"
              >
                {formatQty(quantity)}
              </span>
            ) : (
              <QtyCellInput
                initial={quantity}
                row={rowIndex}
                col={colIndex}
                selected={selected}
                status={status}
                disabled={!editable}
                preview={preview}
                onPreview={onPreview}
                onCommit={(v, fillSelection) =>
                  onCellCommit(row.product_external_id, d, v, fillSelection)
                }
                onPasteRow={(text) => {
                  if (editable) onPasteRow(row.product_external_id, d, text);
                }}
                onSelectMouseDown={(shiftKey) => {
                  if (editable) onCellMouseDown(rowIndex, colIndex, shiftKey);
                }}
              />
            )}
            <DayPricePopover
              dateIso={d}
              quantity={quantity}
              asp={row.asp}
              currency={currency}
              levelId={levelId}
              level={level}
              levelPrice={levelId ? row.prices_by_level[levelId] : undefined}
              label={pricingLabelsByDate[d]}
            >
              <span
                style={{ color: "var(--muted-foreground)" }}
                className="block w-full text-right text-[10px] tabular-nums px-1.5 mt-0.5 cursor-pointer hover:underline decoration-dotted underline-offset-2"
              >
                {unit && quantity > 0 ? formatMoneyShort(unit.price * quantity, currency) : "—"}
              </span>
            </DayPricePopover>
          </td>
        );
      })}
      <td
        style={{
          color: delta === 0 ? "var(--muted-foreground)" : delta > 0 ? "var(--destructive)" : "var(--foreground)",
        }}
        className="px-2 py-1.5 text-right font-medium tabular-nums"
        title={`Sum ${rowSum} vs target ${row.month_target}`}
      >
        <span className="inline-flex items-center gap-1">
          {onDistributeRow && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDistributeRow(row)}
              title="Distribute remaining across non-promo, non-manual days"
              style={{ color: "var(--muted-foreground)" }}
              className="h-5 w-5 opacity-40 hover:opacity-100"
            >
              <Wand2 className="h-3 w-3" />
            </Button>
          )}
          <span>
            {formatQty(rowSum)} / {formatQty(row.month_target)}
          </span>
        </span>
      </td>
    </tr>
  );
}

// Rows are pure renders of their props; memo keeps a single-cell edit from
// re-rendering every other product row (only the edited row gets a new
// object identity from useCellSaver).
export default memo(DailyProductRow);
